<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import {
  WHATSAPP_TEMPLATE_TYPES,
  WHATSAPP_TEMPLATE_CATEGORIES,
  WHATSAPP_LANGUAGE_OPTIONS,
  WHATSAPP_NAME_PATTERN,
} from '../constants/whatsappTemplateTypes';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue', 'next', 'cancel']);

const { t } = useI18n();

const formData = reactive({
  name: props.modelValue.name || '',
  language: props.modelValue.language || 'pt_BR',
  category: props.modelValue.category || 'UTILITY',
  template_type: props.modelValue.template_type || 'TEXT',
});

// Sync formData with props
watch(
  () => props.modelValue,
  newVal => {
    formData.name = newVal.name || '';
    formData.language = newVal.language || 'pt_BR';
    formData.category = newVal.category || 'UTILITY';
    formData.template_type = newVal.template_type || 'TEXT';
  },
  { deep: true }
);

watch(
  () => formData.template_type,
  () => {
    emit('update:modelValue', { ...formData });
  }
);

const nameError = computed(() => {
  if (!formData.name) return '';

  if (formData.name.length > 512) {
    return 'Name must be 512 characters or less';
  }

  if (!WHATSAPP_NAME_PATTERN.test(formData.name)) {
    return t('WHATSAPP_TEMPLATES.CREATE.FORM.NAME_HINT');
  }

  return '';
});

const isValid = computed(() => {
  return (
    formData.name &&
    formData.language &&
    formData.category &&
    formData.template_type &&
    !nameError.value
  );
});

const selectedLanguageLabel = computed(() => {
  const selected = WHATSAPP_LANGUAGE_OPTIONS.find(
    opt => opt.value === formData.language
  );
  return selected ? selected.label : 'Select language';
});

const sanitizeName = () => {
  // Auto-convert to lowercase and replace invalid chars
  formData.name = formData.name
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_/, '');
};

const handleNameInput = () => {
  sanitizeName();
};

const handleLanguageSelect = value => {
  formData.language = value;
};

const handleNext = () => {
  if (!isValid.value) return;

  emit('update:modelValue', { ...formData });
  emit('next');
};

const handleCancel = () => emit('cancel');
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Template Name -->
    <Input
      v-model="formData.name"
      :label="t('WHATSAPP_TEMPLATES.CREATE.FORM.NAME_LABEL')"
      :placeholder="t('WHATSAPP_TEMPLATES.CREATE.FORM.NAME_PLACEHOLDER')"
      :message="nameError || t('WHATSAPP_TEMPLATES.CREATE.FORM.NAME_HINT')"
      :message-type="nameError ? 'error' : 'info'"
      @blur="sanitizeName"
      @input="handleNameInput"
    />

    <!-- Language Select -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.LANGUAGE_LABEL') }}
      </label>
      <SelectMenu
        :model-value="formData.language"
        :label="selectedLanguageLabel"
        :options="WHATSAPP_LANGUAGE_OPTIONS"
        @update:model-value="handleLanguageSelect"
      />
    </div>

    <!-- Category -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.CATEGORY_LABEL') }}
      </label>
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label
          v-for="cat in WHATSAPP_TEMPLATE_CATEGORIES"
          :key="cat.value"
          class="flex flex-col p-3 border-2 rounded-lg cursor-pointer transition-all hover:border-n-brand hover:bg-n-alpha-2"
          :class="[
            formData.category === cat.value
              ? 'border-n-brand bg-n-alpha-2'
              : 'border-n-weak',
          ]"
        >
          <input
            v-model="formData.category"
            type="radio"
            :value="cat.value"
            class="sr-only"
          />
          <span class="font-medium text-sm text-n-slate-12">
            {{ t(cat.labelKey) }}
          </span>
          <span class="text-xs text-n-slate-11 mt-1">
            {{ t(cat.descriptionKey) }}
          </span>
        </label>
      </div>
    </div>

    <!-- Template Type -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.TYPE_LABEL') }}
      </label>
      <div class="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <label
          v-for="type in WHATSAPP_TEMPLATE_TYPES"
          :key="type.value"
          class="flex flex-col p-3 border-2 rounded-lg transition-all"
          :class="[
            type.enabled
              ? 'cursor-pointer hover:border-n-brand hover:bg-n-alpha-2'
              : 'opacity-50 cursor-not-allowed bg-n-slate-2',
            formData.template_type === type.value
              ? 'border-n-brand bg-n-alpha-2'
              : 'border-n-weak',
          ]"
        >
          <input
            v-model="formData.template_type"
            type="radio"
            :value="type.value"
            :disabled="!type.enabled"
            class="sr-only"
          />
          <div class="flex items-center gap-2 mb-1">
            <i
              class="text-lg"
              :class="[
                type.icon,
                type.enabled && formData.template_type === type.value
                  ? 'text-n-brand'
                  : type.enabled
                    ? 'text-n-slate-12'
                    : 'text-n-slate-9',
              ]"
            />
            <span
              class="font-medium text-sm"
              :class="[
                type.enabled && formData.template_type === type.value
                  ? 'text-n-brand'
                  : type.enabled
                    ? 'text-n-slate-12'
                    : 'text-n-slate-11',
              ]"
            >
              {{ t(type.labelKey) }}
            </span>
          </div>
          <span class="text-xs text-n-slate-11 leading-tight">
            {{ t(type.descriptionKey) }}
          </span>
          <div
            v-if="!type.enabled"
            class="text-xs text-n-blue-9 font-medium mt-1"
          >
            {{ t('WHATSAPP_TEMPLATES.CREATE.WIZARD.COMING_SOON') }}
          </div>
        </label>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-between w-full gap-3 pt-2">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('WHATSAPP_TEMPLATES.CREATE.WIZARD.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('WHATSAPP_TEMPLATES.CREATE.WIZARD.NEXT')"
        icon="i-lucide-arrow-right"
        icon-position="right"
        class="w-full"
        type="button"
        :disabled="!isValid"
        @click="handleNext"
      />
    </div>
  </div>
</template>
